// src/components/common/ProtectedRoute.jsx
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getEffectiveRole } from "../../utils/navigation";

// ── Map role → home route ──────────────────────────────────
export function getDashboardPath(roleOrProfile) {
  const role = typeof roleOrProfile === "object"
    ? getEffectiveRole(roleOrProfile)
    : (roleOrProfile || "").toString().toLowerCase();

  const map = {
    admin:          "/admin/dashboard",
    hr:             "/hr/dashboard",
    lead:           "/lead/dashboard",
    "finance-lead": "/finance/dashboard",
    finance:        "/finance/dashboard",
    employee:       "/employee/dashboard",
  };

  return map[role] ?? "/employee/dashboard";
}

// ── Full-screen auth loading screen ───────────────────────
function AuthLoader({ message = "Verifying your session…" }) {
  return (
    <div className="hrms-loading-screen" role="status" aria-live="polite">
      <div className="hrms-spinner" aria-hidden="true" />
      <p className="hrms-loading-msg">{message}</p>
    </div>
  );
}

// ── ProtectedRoute ─────────────────────────────────────────
export default function ProtectedRoute({ children, role }) {
  const { currentUser, userProfile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <AuthLoader message="Loading your account…" />;
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (!userProfile) {
    return (
      <div className="hrms-loading-screen">
        <div className="hrms-spinner" />
        <p className="hrms-loading-msg">Setting up your profile…</p>
        <p className="hrms-loading-sub">
          If this persists, ask your administrator to complete your account setup.
        </p>
      </div>
    );
  }

  const effectiveRole = getEffectiveRole(userProfile);

  if (role) {
    const allowedRoles = (Array.isArray(role) ? role : [role]).map((r) => (r || "").toLowerCase());
    const userRole = (userProfile.role || "").toLowerCase();
    const effRole = (effectiveRole || "").toLowerCase();

    if (!allowedRoles.includes(userRole) && !allowedRoles.includes(effRole)) {
      return <Navigate to={getDashboardPath(userProfile)} replace />;
    }
  }

  return children;
}
